import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { tomos } from '../data/constants';
import { Check, ChevronDown, Trophy, BookOpen, Target, Layers, Users, MapPin } from 'lucide-react';
export function ProductOverview() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const includes = ['Planificación semanal lista por trimestre', 'Ejercicios con descripción técnica completa', 'Protocolos de seguridad integrados en cada ficha', 'Documentación legal lista para firmar', 'Fichas de evaluación incluidas'];
  const valueBullets = [{
    icon: BookOpen,
    label: '+200 recursos técnicos'
  }, {
    icon: Target,
    label: '5 Tomos completos'
  }, {
    icon: Layers,
    label: 'De la Semana 1 a la 40'
  }, {
    icon: Check,
    label: 'Basado en ACOG 2024 y CSEP 2023'
  }, {
    icon: Users,
    label: 'Creado para profesionales'
  }, {
    icon: MapPin,
    label: 'Adaptado para Argentina'
  }];
  return <section className="py-16 md:py-24 px-4 max-w-5xl mx-auto" data-id="element-320">
      {/* SOLUTION INTRO */}
      <div className="text-center mb-12 md:mb-16" data-id="element-321">
        <p className="text-primary font-bold uppercase tracking-widest text-[11px] md:text-sm mb-3 flex items-center justify-center gap-2" data-id="element-322">
          <Trophy className="w-4 h-4" data-id="element-323" /> La solución
        </p>
        <h2 className="text-2xl md:text-5xl font-extrabold mb-4 leading-tight max-w-3xl mx-auto text-slate-900" data-id="element-324">
          El sistema completo para{' '}
          <span className="text-primary" data-id="element-325">resolver tus clases</span>
        </h2>
        <p className="text-base md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed font-medium" data-id="element-326">
          5 Tomos + 5 Bonos · De la Semana 1 a la Semana 40 · Todo resuelto
        </p>
      </div>

      {/* VALUE BULLETS GRID */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-14 md:mb-16" data-id="element-327">
        {valueBullets.map(({
        icon: Icon,
        label
      }, i) => <div key={i} className="bg-white border border-slate-200 shadow-sm hover:border-primary/40 transition-colors rounded-xl p-4 md:p-5 flex items-center gap-3" data-id="element-328">
            <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0" data-id="element-329">
              <Icon className="w-4 h-4 text-primary" data-id="element-330" />
            </div>
            <span className="text-sm md:text-base font-bold text-slate-800 leading-tight" data-id="element-331">
              {label}
            </span>
          </div>)}
      </div>

      {/* 5 TOMOS */}
      <div className="mb-14 md:mb-16" data-id="element-332">
        <div className="text-center mb-8 md:mb-10" data-id="element-333">
          <h3 className="text-2xl md:text-4xl font-extrabold mb-3 leading-tight text-slate-900" data-id="element-334">
            Los 5 Tomos del Sistema.{' '}
            <span className="text-primary" data-id="element-335">Todo el embarazo cubierto.</span>
          </h3>
          <p className="text-sm md:text-base text-slate-500 font-medium" data-id="element-336">
            Tocá un tomo para ver el contenido
          </p>
        </div>

        <div className="space-y-3" data-id="element-337">
          {tomos.map((t, i) => {
          const isOpen = openIndex === i;
          return <div key={t.id} className={`rounded-2xl border overflow-hidden transition-colors duration-200 ${isOpen ? 'bg-white border-primary shadow-[0_5px_20px_rgba(37,99,235,0.1)]' : 'bg-white border-slate-200 hover:border-primary/40'}`} data-id="element-338">
                <button className="w-full px-5 py-4 md:px-7 md:py-5 text-left flex items-center justify-between gap-4 focus:outline-none" onClick={() => setOpenIndex(isOpen ? null : i)} data-id="element-339">
                  <div className="flex items-center gap-3 md:gap-4 min-w-0" data-id="element-340">
                    <span className="text-2xl md:text-3xl shrink-0" data-id="element-341">
                      {t.icon}
                    </span>
                    <div className="min-w-0" data-id="element-342">
                      <div className="font-bold text-slate-900 text-base md:text-lg leading-tight" data-id="element-343">
                        Tomo {i + 1} — {t.name}
                      </div>
                      <div className="text-xs md:text-sm text-primary font-bold mt-0.5" data-id="element-344">
                        {t.months}
                      </div>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-primary shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-345" />
                </button>

                <AnimatePresence data-id="element-346">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-347">
                      <div className="px-5 pb-5 md:px-7 md:pb-6 text-slate-600 text-sm md:text-base leading-relaxed border-t border-slate-100 pt-4 font-medium" data-id="element-348">
                        <ul className="space-y-2" data-id="element-349">
                          {t.content.split('. ').filter(Boolean).map((line, idx) => <li key={idx} className="flex items-start gap-2" data-id="element-350">
                                <span className="text-primary mt-0.5" data-id="element-351">→</span>
                                <span data-id="element-352">{line.trim()}</span>
                              </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>

      {/* WHAT EACH TOMO INCLUDES */}
      <div data-id="element-353">
        <div className="text-center mb-8 md:mb-10" data-id="element-354">
          <h3 className="text-2xl md:text-3xl font-extrabold leading-tight max-w-3xl mx-auto text-slate-900" data-id="element-355">
            Cada tomo viene listo para{' '}
            <span className="text-primary" data-id="element-356">entrar al aula y usar</span>
          </h3>
        </div>

        <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-6 md:p-8" data-id="element-357">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" data-id="element-358">
            {includes.map((item, i) => <div key={i} className="flex items-start gap-2.5" data-id="element-359">
                <div className="mt-0.5 bg-primary/10 p-1 rounded-full shrink-0" data-id="element-360">
                  <Check className="w-3 h-3 text-primary" data-id="element-361" />
                </div>
                <span className="text-slate-700 text-sm md:text-base font-medium" data-id="element-362">
                  {item}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}